import type { AgentAccent } from '@ralph/contracts'
import { AgentAccentSchema } from '@ralph/contracts'
import clsx from 'clsx'
import { ACCENT_INK } from '@/components/marketplace/accents.ts'
import { useT } from '@/i18n/index.ts'

/**
 * Choosing the agent's colour.
 *
 * The palette itself lives in `marketplace/accents.ts` — one module turns a token into a
 * colour for both the gallery and the builder, so a maker's preview of their choice is
 * literally the tile a reader will see.
 *
 * A pressed-button group rather than swatches alone: the colour's NAME is part of every
 * option, because a control whose only signal is hue is unusable to a reader who cannot
 * distinguish them and unlabelable to a screen reader (AGM-124).
 */
export const AccentPicker = ({
  value,
  disabled,
  onChange
}: {
  value: AgentAccent
  disabled?: boolean
  onChange: (accent: AgentAccent) => void
}) => {
  // Read in the body, never captured at module scope: a colour name is copy, and a capture
  // taken at import would keep the language the bundle evaluated in (PRD-27 F-3).
  const accents = useT().marketplace.maker.accents

  return (
    <div className="flex flex-wrap gap-1.5">
      {AgentAccentSchema.options.map((accent) => (
        <button
          key={accent}
          type="button"
          disabled={disabled}
          aria-pressed={accent === value}
          onClick={() => onChange(accent)}
          className={clsx(
            'flex h-[30px] items-center gap-2 rounded-full border px-2.5 text-[12.5px] tracking-[-0.01em]',
            'transition-[color,border-color,background-color,box-shadow,transform] duration-200',
            'disabled:pointer-events-none disabled:opacity-45',
            accent === value
              ? 'border-rule-strong bg-card-solid text-fg shadow-glass'
              : 'border-rule text-fg-muted hover:-translate-y-px hover:border-rule-strong hover:text-fg'
          )}
        >
          {/* The swatch is the same fill an agent's identity tile takes, with the same lip and
            cast shadow at a smaller scale, so the choice is shown in the material it will be
            rendered in rather than as a flat square of ink. */}
          <span
            aria-hidden
            style={{ background: ACCENT_INK[accent] }}
            className="h-[14px] w-[14px] rounded-[5px] shadow-[inset_0_-1px_2px_color-mix(in_srgb,var(--shadow-ink)_26%,transparent)]"
          />
          {accents[accent]}
        </button>
      ))}
    </div>
  )
}
